import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { WA_LINK } from "../lib/data";
import { useLang } from "../lib/i18n";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const { t } = useLang();
  const rm = useReducedMotion();
  const D = rm ? 0 : 1.9;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], [0, rm ? 0 : 140]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: videoY }} className="absolute inset-0 scale-[1.08]">
        <video
          className="w-full h-full object-cover"
          src="/assets/bg.mp4"
          poster="/assets/hero-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/55 to-ink" aria-hidden="true" />
      <div className="absolute inset-0 blueprint-lines opacity-60" aria-hidden="true" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 min-h-screen flex flex-col justify-center pt-28 pb-14">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: D, duration: 0.7, ease: EASE }}
          className="inline-flex"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-cream/20 bg-cream/5 backdrop-blur-md px-4 py-2 text-xs sm:text-sm font-semibold text-cream/85">
            <span className="w-1.5 h-1.5 rounded-full bg-brass" />
            {t.heroEyebrow}
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: D + 0.15, duration: 0.7, ease: EASE }}
          className="flex items-center gap-3 mt-6"
        >
          <span className="shine-hover inline-flex items-center rounded-2xl bg-cream/95 px-3 py-1.5 shadow-lg">
            <img src="/assets/lucky-logo.png" alt="Lucky Cement logo" className="h-9 w-auto" />
          </span>
          <span className="shine-hover inline-flex items-center rounded-2xl bg-cream/10 backdrop-blur-md border border-cream/15 px-3 py-1.5">
            <img src="/assets/bestway-logo.png" alt="Bestway Cement logo" className="h-8 w-auto rounded-lg" />
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold tracking-[-0.03em] leading-[1.02] text-5xl sm:text-6xl lg:text-7xl mt-8 max-w-4xl">
          {t.heroLines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={rm ? false : { y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: D + 0.25 + i * 0.13, duration: 0.95, ease: EASE }}
              >
                {i === 1 ? (
                  <em className="font-serif italic font-medium text-brasslight">{line}</em>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: D + 0.7, duration: 0.7, ease: EASE }}
          className="mt-6 font-display text-lg md:text-xl font-bold text-cream/90"
        >
          {t.heroSub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: D + 0.85, duration: 0.7, ease: EASE }}
          className="mt-9"
        >
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-order-whatsapp-button"
            className="pulse-ring inline-flex items-center gap-2.5 rounded-full bg-brass px-8 py-4 font-display text-base font-bold text-ink transition-all duration-300 hover:bg-brasslight active:scale-95"
          >
            <MessageCircle size={20} strokeWidth={2.4} />
            {t.heroCta}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: D + 1.05, duration: 0.8, ease: EASE }}
          className="mt-auto pt-20 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <p className="max-w-md text-sm md:text-base text-cream/65 leading-relaxed">{t.heroPara}</p>
          <div className="flex flex-wrap gap-3">
            {t.heroBadges.map((b) => (
              <span
                key={b}
                className="rounded-full border border-brass/40 bg-brass/10 px-4 py-2 text-xs font-bold text-brasslight"
              >
                {b}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
